import { useState } from 'react'
import { api, errorMessage, useQuery } from '../api.js'
import { Alert, Button, Field, Input, Panel, Pill } from '../components/ui.js'

type Produto = Awaited<ReturnType<typeof api.catalog.products.query>>[number]
type Detalhe = Awaited<ReturnType<typeof api.catalog.product.query>>

const ROTULO_FASE: Record<string, string> = {
  ideia: 'ideia',
  especificacao: 'especificacao',
  construcao: 'construcao',
  beta_interno: 'beta interno',
  producao: 'producao',
  manutencao: 'manutencao',
  fim_de_vida: 'fim de vida',
}

const TOM_CRITICIDADE: Record<string, 'crit' | 'warn' | 'neutral'> = {
  critico: 'crit',
  importante: 'warn',
  auxiliar: 'neutral',
}

/**
 * Catalogo de produtos.
 *
 * A tela existe para responder duas perguntas que o GitHub nao responde: em
 * que fase cada sistema esta, e onde ele roda. A segunda e a que liga o
 * catalogo a frota — e o que faz "o que cai se eu reiniciar isto" ter
 * resposta.
 */
export function Catalog() {
  const [aberto, setAberto] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const produtos = useQuery(() => api.catalog.products.query(), [])
  const fases = useQuery(() => api.catalog.phases.query(), [])

  async function comErro(fn: () => Promise<unknown>) {
    setErro(null)
    try {
      await fn()
      await produtos.refetch()
      await fases.refetch()
    } catch (err) {
      setErro(errorMessage(err))
    }
  }

  const lista = produtos.data ?? []

  return (
    <div className="space-y-6">
      {erro && <Alert>{erro}</Alert>}

      <OcupacaoDasFases dados={fases.data} />

      <Panel
        title={`Produtos — ${lista.length}`}
        action={
          <div className="flex gap-2">
            <Button onClick={() => comErro(() => api.catalog.checkHealth.mutate())}>
              Verificar saude agora
            </Button>
            <Button variant="primary" onClick={() => setCriando(true)}>
              Novo produto
            </Button>
          </div>
        }
      >
        {criando && <NovoProduto onFeito={() => { setCriando(false); void comErro(async () => {}) }} onClose={() => setCriando(false)} />}

        {lista.length === 0 ? (
          <p className="text-sm text-ink-3">
            Nenhum produto cadastrado. O catalogo e o que permite responder "o que cai se eu
            reiniciar este servidor" — comece pelos sistemas que ja rodam na frota.
          </p>
        ) : (
          <ul className="divide-y divide-line/60">
            {lista.map((p) => (
              <LinhaProduto
                key={p.id}
                produto={p}
                aberto={aberto === p.id}
                onAbrir={() => setAberto(aberto === p.id ? null : p.id)}
                onAcao={comErro}
              />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}

function OcupacaoDasFases({
  dados,
}: {
  dados?: { fases: Array<{ fase: string; quantidade: number; limite: number | null; acima: boolean }> } | null
}) {
  if (!dados) return null
  const acima = dados.fases.filter((f) => f.acima)

  return (
    <Panel title="Ciclo de vida">
      <div className="flex flex-wrap gap-2">
        {dados.fases.map((f) => (
          <div
            key={f.fase}
            className={`rounded border px-3 py-2 text-sm ${
              f.acima ? 'border-warn bg-warn-soft text-warn' : 'border-line text-ink-2'
            }`}
          >
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              {ROTULO_FASE[f.fase] ?? f.fase}
            </div>
            <div className="mt-0.5">
              <span className="text-lg font-semibold">{f.quantidade}</span>
              {f.limite !== null && <span className="text-xs text-ink-3"> / {f.limite}</span>}
            </div>
          </div>
        ))}
      </div>
      {acima.length > 0 && (
        <p className="mt-4 text-sm text-warn">
          {acima.map((f) => ROTULO_FASE[f.fase]).join(', ')} acima do limite. O painel avisa e nao
          impede — mas com muita coisa em andamento ao mesmo tempo, nenhuma chega em producao.
        </p>
      )}
    </Panel>
  )
}

function LinhaProduto({
  produto,
  aberto,
  onAbrir,
  onAcao,
}: {
  produto: Produto
  aberto: boolean
  onAbrir: () => void
  onAcao: (fn: () => Promise<unknown>) => Promise<void>
}) {
  return (
    <li className="py-3">
      <button onClick={onAbrir} className="flex w-full items-start gap-3 text-left">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-ink">{produto.name}</span>
            <Pill tone={TOM_CRITICIDADE[produto.criticality] ?? 'neutral'}>{produto.criticality}</Pill>
            <Pill tone={produto.phase === 'producao' ? 'ok' : 'accent'}>
              {ROTULO_FASE[produto.phase] ?? produto.phase}
            </Pill>
          </div>
          {produto.impactNote && <p className="mt-1 text-sm text-ink-2">{produto.impactNote}</p>}
          <div className="mt-1 flex flex-wrap gap-3 text-xs text-ink-3">
            {produto.ambientes.map((a) => (
              <span key={a.id}>
                {a.name}:{' '}
                {a.hostname ? (
                  <span className="font-mono text-ink-2">{a.hostname}</span>
                ) : (
                  <span className="italic">fora da frota</span>
                )}
              </span>
            ))}
            {produto.ambientes.length === 0 && <span className="italic">sem ambiente cadastrado</span>}
          </div>
        </div>
        <span className="mt-1 text-ink-3">{aberto ? '−' : '+'}</span>
      </button>

      {aberto && <DetalheProduto productId={produto.id} onAcao={onAcao} />}
    </li>
  )
}

function DetalheProduto({
  productId,
  onAcao,
}: {
  productId: string
  onAcao: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const detalhe = useQuery(() => api.catalog.product.query({ id: productId }), [productId])
  const frota = useQuery(() => api.fleet.servers.query(), [])
  const [novoAmbiente, setNovoAmbiente] = useState(false)

  if (!detalhe.data) return <p className="mt-3 text-sm text-ink-3">Carregando...</p>
  const d = detalhe.data as Detalhe

  return (
    <div className="mt-4 space-y-4 border-l-2 border-line pl-4">
      <NotaDeSaude saude={d.saude} />

      <div>
        <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3">Onde roda</h4>
        {d.ondeRoda.length === 0 ? (
          <p className="mt-1 text-sm text-ink-3">Nenhum ambiente cadastrado.</p>
        ) : (
          <ul className="mt-2 space-y-1.5 text-sm">
            {d.ondeRoda.map((a) => (
              <li key={a.environmentId} className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-ink">{a.ambiente}</span>
                {a.servidor ? (
                  <>
                    <span className="text-ink-3">em</span>
                    <span className="font-mono text-ink-2">{a.servidor.hostname}</span>
                    <Pill tone={a.servidor.powerState === 'on' ? 'ok' : 'crit'}>
                      {a.servidor.powerState === 'on' ? 'ligado' : a.servidor.powerState}
                    </Pill>
                    {!a.temAgente && <Pill tone="neutral">sem agente</Pill>}
                  </>
                ) : (
                  <span className="text-ink-3 italic">fora da frota</span>
                )}
                {a.url && (
                  <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-accent underline underline-offset-2">
                    abrir
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}

        {novoAmbiente ? (
          <NovoAmbiente
            productId={productId}
            servidores={frota.data ?? []}
            onFeito={async () => {
              setNovoAmbiente(false)
              await detalhe.refetch()
              await onAcao(async () => {})
            }}
            onClose={() => setNovoAmbiente(false)}
          />
        ) : (
          <button
            onClick={() => setNovoAmbiente(true)}
            className="mt-2 text-xs text-accent underline underline-offset-2"
          >
            + ambiente
          </button>
        )}
      </div>

      {(d.dependeDe.length > 0 || d.quemDependeDele.length > 0) && (
        <div className="grid gap-4 sm:grid-cols-2">
          {d.dependeDe.length > 0 && (
            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3">Depende de</h4>
              <ul className="mt-1 text-sm text-ink-2">
                {d.dependeDe.map((x) => (
                  <li key={x.productId}>
                    {x.nome} <span className="text-ink-3">({x.kind})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {d.quemDependeDele.length > 0 && (
            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                Sente se este cair
              </h4>
              <ul className="mt-1 text-sm text-ink-2">
                {d.quemDependeDele.map((x) => (
                  <li key={x.productId}>
                    {x.nome} <span className="text-ink-3">({x.kind})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <RequisitosDeProducao fase={d.phase} requisitos={d.requisitosProducao} productId={productId} onAcao={onAcao} />
    </div>
  )
}

function NotaDeSaude({ saude }: { saude: Detalhe['saude'] }) {
  const tom = saude.nota === null ? 'neutral' : saude.nota >= 80 ? 'ok' : saude.nota >= 50 ? 'warn' : 'crit'
  return (
    <div>
      <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3">Nota de saude</h4>
      <div className="mt-1 flex items-center gap-3">
        <Pill tone={tom}>{saude.nota === null ? 'sem dados' : `${saude.nota}/100`}</Pill>
        <span className="text-xs text-ink-3">
          cobertura {saude.cobertura}% dos criterios
          {saude.cobertura < 50 && ' — nota diz pouco com esta cobertura'}
        </span>
      </div>
      {saude.medidos.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-ink-2">
          {saude.medidos.map((m) => (
            <li key={m.fator}>
              {m.fator}: {m.detalhe}
            </li>
          ))}
        </ul>
      )}
      {saude.naoMedidos.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-ink-3">
            {saude.naoMedidos.length} criterio(s) ainda nao medivel(is)
          </summary>
          <ul className="mt-1 space-y-0.5 pl-3 text-xs text-ink-3">
            {saude.naoMedidos.map((m) => (
              <li key={m.fator}>
                {m.fator} ({m.peso}%): {m.porque}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

function RequisitosDeProducao({
  fase,
  requisitos,
  productId,
  onAcao,
}: {
  fase: string
  requisitos: Detalhe['requisitosProducao']
  productId: string
  onAcao: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const [motivo, setMotivo] = useState('')
  const pendentes = requisitos.filter((r) => r.atendido === false)
  const jaEmProducao = fase === 'producao' || fase === 'manutencao'

  if (jaEmProducao) return null

  return (
    <div>
      <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
        Para promover a producao
      </h4>
      <ul className="mt-1 space-y-1 text-sm">
        {requisitos.map((r) => (
          <li key={r.requisito} className="flex items-start gap-2">
            <span className={r.atendido === true ? 'text-ok' : r.atendido === false ? 'text-crit' : 'text-ink-3'}>
              {r.atendido === true ? '✓' : r.atendido === false ? '✗' : '?'}
            </span>
            <span className="text-ink-2">
              {r.requisito} — <span className="text-ink-3">{r.detalhe}</span>
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        {pendentes.length > 0 && (
          <Field label="Justificativa" hint="Fica na trilha, junto do seu nome.">
            <Input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: backup conferido na mao em 31/08"
            />
          </Field>
        )}
        <Button
          variant={pendentes.length ? 'danger' : 'primary'}
          disabled={pendentes.length > 0 && motivo.trim().length < 5}
          onClick={() =>
            onAcao(() =>
              api.catalog.setPhase.mutate({
                productId,
                phase: 'producao',
                forcar: pendentes.length > 0,
                motivo: pendentes.length > 0 ? motivo : undefined,
              }),
            )
          }
        >
          {pendentes.length ? 'Promover mesmo assim' : 'Promover a producao'}
        </Button>
      </div>
    </div>
  )
}

function NovoProduto({ onFeito, onClose }: { onFeito: () => void; onClose: () => void }) {
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [impactNote, setImpactNote] = useState('')
  const [criticality, setCriticality] = useState<'critico' | 'importante' | 'auxiliar'>('auxiliar')
  const [erro, setErro] = useState<string | null>(null)

  const slugOk = /^[a-z0-9-]{2,80}$/.test(slug)

  return (
    <div className="mb-5 rounded border border-line bg-ground p-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nome">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Fiscal Hub" />
        </Field>
        <Field label="Identificador" hint="Minusculas, numeros e hifen.">
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="fiscal-hub"
          />
        </Field>
        <Field
          label="Qual dor resolve"
          hint="Uma frase. Produto que ninguem descreve em uma frase costuma ser produto que ninguem deveria construir."
        >
          <Input
            value={impactNote}
            onChange={(e) => setImpactNote(e.target.value)}
            placeholder="Emite NF-e e NFS-e sem depender da Focus"
          />
        </Field>
        <Field label="Criticidade">
          <select
            value={criticality}
            onChange={(e) => setCriticality(e.target.value as typeof criticality)}
            className="w-full rounded border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
          >
            <option value="auxiliar">auxiliar</option>
            <option value="importante">importante</option>
            <option value="critico">critico</option>
          </select>
        </Field>
      </div>

      {erro && <div className="mt-3"><Alert>{erro}</Alert></div>}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          disabled={!slugOk || name.trim().length < 2}
          onClick={async () => {
            setErro(null)
            try {
              await api.catalog.createProduct.mutate({
                slug,
                name,
                criticality,
                impactNote: impactNote || undefined,
              })
              onFeito()
            } catch (err) {
              setErro(errorMessage(err))
            }
          }}
        >
          Criar
        </Button>
      </div>
    </div>
  )
}

function NovoAmbiente({
  productId,
  servidores,
  onFeito,
  onClose,
}: {
  productId: string
  servidores: Array<{ id: string; hostname: string }>
  onFeito: () => Promise<void>
  onClose: () => void
}) {
  const [name, setName] = useState('producao')
  const [url, setUrl] = useState('')
  const [healthUrl, setHealthUrl] = useState('')
  const [serverId, setServerId] = useState('')
  const [erro, setErro] = useState<string | null>(null)

  return (
    <div className="mt-3 rounded border border-line bg-ground p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Ambiente">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="producao" />
        </Field>
        <Field label="Servidor da frota" hint="Vazio = roda fora da frota (Vercel, Base44...).">
          <select
            value={serverId}
            onChange={(e) => setServerId(e.target.value)}
            className="w-full rounded border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
          >
            <option value="">fora da frota</option>
            {servidores.map((s) => (
              <option key={s.id} value={s.id}>{s.hostname}</option>
            ))}
          </select>
        </Field>
        <Field label="URL">
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." />
        </Field>
        <Field label="/healthz" hint="Sem isto, a nota de saude nao mede disponibilidade.">
          <Input value={healthUrl} onChange={(e) => setHealthUrl(e.target.value)} placeholder="https://.../healthz" />
        </Field>
      </div>

      {erro && <div className="mt-3"><Alert>{erro}</Alert></div>}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          disabled={name.trim().length < 1}
          onClick={async () => {
            setErro(null)
            try {
              await api.catalog.createEnvironment.mutate({
                productId,
                name,
                url: url || undefined,
                healthUrl: healthUrl || undefined,
                serverId: serverId || undefined,
              })
              await onFeito()
            } catch (err) {
              setErro(errorMessage(err))
            }
          }}
        >
          Adicionar
        </Button>
      </div>
    </div>
  )
}
